import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, X, Download, FileText, AlertCircle, Loader2, Edit2, Save, XCircle, Briefcase, GraduationCap, Clock } from 'lucide-react';
import { getApiUrlsWithFallback } from '../../../config/api';
import ResumePreview, { downloadResume } from './ResumePreview';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Stack } from '@/Components/ui/layout';
import { Inline } from '@/Components/ui/layout';
import { Heading } from '@/Components/ui/typography';
import { Text } from '@/Components/ui/typography';
import { Badge } from '@/Components/ui/badge';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { cn } from '@/lib/utils';

function CandidateProfilePopup({ candidate, onClose }) {
  const [resumeUrl, setResumeUrl] = useState(candidate.resume_path);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (candidate) {
      fetchResumeUrl();
      // fetchProfileData();
    }
  }, [candidate]);

  const fetchProfileData = async () => {
    if (!candidate.candidate_email) return;

    try {
      setProfileLoading(true);
      const urls = getApiUrlsWithFallback(`/api/get_user_profile?email=${encodeURIComponent(candidate.candidate_email)}`);

      for (const url of urls) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            setProfileData(data);
            break;
          }
        } catch (fetchError) {
          continue;
        }
      }
    } catch (err) {
      console.error('Failed to load profile data:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchResumeUrl = async () => {
    try {
      setLoading(true);
      setError(null);

      if (candidate.resume_path) {
        setResumeUrl(candidate.resume_path);
        setLoading(false);
        return;
      }

      if (candidate.candidate_email && candidate.job_id) {
        const urls = getApiUrlsWithFallback(`/api/get_candidate_resume?email=${encodeURIComponent(candidate.candidate_email)}&jobId=${candidate.job_id}`);

        let success = false;
        for (const url of urls) {
          try {
            const response = await fetch(url);

            if (response.ok) {
              const data = await response.json();
              setResumeUrl(data.resume_url);
              success = true;
              break;
            }
          } catch (fetchError) {
            continue;
          }
        }

        if (!success) {
          setError('Resume not found');
        }
      } else {
        const missingFields = [];
        if (!candidate.candidate_email) missingFields.push('email');
        if (!candidate.job_id) missingFields.push('job_id');
        setError(`Missing required fields: ${missingFields.join(', ')}`);
      }
    } catch (err) {
      setError('Failed to load resume');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleEdit = () => {
    const currentData = profileData || candidate;
    setEditedData({
      phone: currentData?.phone || '',
      cur_ctc: currentData?.cur_ctc || '',
      exp_ctc: currentData?.exp_ctc || '',
      notice_period: currentData?.notice_period || '',
      cur_loc: currentData?.cur_loc || '',
      currentOrganization: currentData?.currentOrganization || '',
      exp_years: currentData?.exp_years || '',
      exp_months: currentData?.exp_months || '',
      relevant_exp_years: currentData?.relevant_exp_years || '',
      relevant_exp_months: currentData?.relevant_exp_months || '',
      qualification: currentData?.qualification || '',
      preferred_location: currentData?.preferred_location || '',
      college: currentData?.college || '',
      note: currentData?.note
    });
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({});
    setSaveError(null);
  };

  // Check if all required fields are filled
  const areAllFieldsFilled = () => {
    const hasValue = (value) => {
      if (value === undefined || value === null || value === '') return false;
      return String(value).trim().length > 0;
    };

    return (
      hasValue(editedData.phone) &&
      hasValue(editedData.cur_ctc) &&
      hasValue(editedData.exp_ctc) &&
      hasValue(editedData.notice_period) &&
      hasValue(editedData.cur_loc) &&
      hasValue(editedData.currentOrganization) &&
      (hasValue(editedData.exp_years) || hasValue(editedData.exp_months)) &&
      hasValue(editedData.qualification) &&
      hasValue(editedData.preferred_location)
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      // Validate data
      if (editedData.cur_ctc && (isNaN(editedData.cur_ctc) || parseFloat(editedData.cur_ctc) < 0)) {
        setSaveError('Current CTC must be a valid positive number');
        setSaving(false);
        return;
      }
      if (editedData.exp_ctc && (isNaN(editedData.exp_ctc) || parseFloat(editedData.exp_ctc) < 0)) {
        setSaveError('Expected CTC must be a valid positive number');
        setSaving(false);
        return;
      }
      const totalExp = 
        (parseInt(editedData.exp_years || 0)) +
        (parseInt(editedData.exp_months || 0) / 12);

      if (totalExp < 0) {
        setSaveError('Experience must be valid');
        setSaving(false);
        return;
      }

      const urls = getApiUrlsWithFallback(`/api/update_user_profile_company?email=${encodeURIComponent(candidate.candidate_email)}`);

      let success = false;
      for (const url of urls) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              email: candidate.candidate_email,
              name: candidate.name,
              application_id: candidate.id,
              job_id: candidate.job_id,
              ...editedData,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            setProfileData(data.candidate);
            success = true;
            setIsEditing(false);
            setEditedData({});
            break;
          } else {
            const errorData = await response.json();
            setSaveError(errorData.error || 'Failed to update profile');
          }
        } catch (fetchError) {
          continue;
        }
      }

      if (!success && !saveError) {
        setSaveError('Failed to update profile. Please try again.');
      }
    } catch (err) {
      setSaveError('An error occurred while saving. Please try again.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const getResumeScore = () => {
    if (candidate.Resume_Score === null || candidate.Resume_Score === undefined) {
      return 'Not available';
    }
    return `${candidate.Resume_Score}%`;
  };

  if (!candidate) return null;

  const currentData = profileData || candidate;

  return (
    <Dialog open={!!candidate} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[1200px] max-h-[90vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="bg-gradient-to-r from-primary to-primary/90 text-primary-foreground p-6 pb-4">
          <DialogTitle className="sr-only">Candidate Profile</DialogTitle>
          <DialogDescription className="sr-only">
            View candidate details and resume
          </DialogDescription>
          <div className="flex items-start justify-between gap-4">
            <Inline spacing="md" align="start" className="gap-4 flex-1">
              <div className="w-16 h-16 rounded-full bg-primary-foreground/20 flex items-center justify-center text-2xl font-semibold text-primary-foreground border-2 border-primary-foreground/30 shadow-lg flex-shrink-0">
                {candidate.name ? candidate.name.charAt(0).toUpperCase() : 'C'}
              </div>
              <Stack spacing="xs" className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <Heading level="h1" className="text-2xl font-bold text-primary-foreground m-0">
                    {candidate.name || 'Unknown Candidate'}
                  </Heading>
                  {/* Edit/Save/Cancel buttons inline with name */}
                  {isEditing ? (
                    <Inline spacing="xs" className="gap-2">
                      <Button
                        onClick={handleSave}
                        disabled={saving || !areAllFieldsFilled()}
                        size="sm"
                        variant="secondary"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-1" />
                            Save
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleCancel}
                        disabled={saving}
                        size="sm"
                        variant="ghost"
                        className="text-primary-foreground hover:bg-primary-foreground/10"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    </Inline>
                  ) : (
                    <Button
                      onClick={handleEdit}
                      size="sm"
                      variant="secondary"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
                <Inline spacing="sm" className="gap-2 flex-wrap">
                  {currentData?.exp_yrs && (
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">
                      {currentData.exp_yrs} YOE
                    </Badge>
                  )}
                  {currentData?.qualification && (
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">
                      {currentData.qualification}
                    </Badge>
                  )}
                </Inline>
              </Stack>
            </Inline>
          </div>
          {saveError && (
            <div className="mt-4 p-3 bg-destructive/20 border border-destructive/30 rounded-md flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-primary-foreground mt-0.5 flex-shrink-0" />
              <Text size="sm" className="text-primary-foreground">
                {saveError}
              </Text>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_450px] gap-6">
            {/* Left Side - Resume Preview */}
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="h-[715px] relative">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-4">
                      <Loader2 className="w-12 h-12 animate-spin text-primary" />
                      <Text size="base" className="text-muted-foreground">Loading resume...</Text>
                    </div>
                  ) : error ? (
                    <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
                      <AlertCircle className="w-12 h-12 text-destructive" />
                      <Text size="base" className="text-center">{error}</Text>
                      <Button variant="outline" onClick={fetchResumeUrl}>
                        Try Again
                      </Button>
                    </div>
                  ) : resumeUrl ? (
                    <div className="h-full flex flex-col">
                      {/* PDF → iframe; .docx → rendered in-page; .doc → download card */}
                      <div className="flex-1 relative min-h-0">
                        <ResumePreview url={resumeUrl} candidateName={candidate.name} />
                      </div>
                      <div className="p-4 border-t bg-muted/30">
                        {/* Downloads through the authenticated backend, never the raw S3 link */}
                        <Button variant="outline" className="w-full" onClick={() => downloadResume(resumeUrl, candidate.name)}>
                          <Download className="w-4 h-4 mr-2" />
                          Download Resume
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-4">
                      <FileText className="w-12 h-12 text-muted-foreground" />
                      <Text size="base" className="text-muted-foreground">Resume not available</Text>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Right Side - Candidate Information */}
            <div className="grid grid-cols-2 gap-3 auto-rows-min">
              {/* Resume Score - Moved to top, spans full width */}
              <Card className="col-span-2">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Application Details</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <Text size="sm" className="text-muted-foreground">Resume Score:</Text>
                      <Badge variant="secondary" className="font-semibold">
                        {getResumeScore()}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Text size="sm" className="text-muted-foreground">Applied:</Text>
                      <Text size="sm" className="font-medium">{formatDate(candidate.created_at)}</Text>
                    </div>
                  </div>
                </CardContent>
              </Card>

              { /* Notes */}
              <Card className="col-span-2">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Notes</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  {isEditing ? (
                    <textarea
                      value={editedData.note || ''}
                      onChange={(e) => handleInputChange('note', e.target.value)}
                      placeholder="Add notes about candidate..."
                      className="w-full text-xs border rounded-md p-2"
                    />
                  ) : (
                    <Text size="xs">
                      {currentData?.note || 'No notes added'}
                    </Text>
                  )}
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Contact</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <Stack spacing="xs" className="gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      {candidate.candidate_email ? (
                        <a href={`mailto:${candidate.candidate_email}`} className="text-xs text-primary hover:underline break-all block mt-0.5">
                          {candidate.candidate_email}
                        </a>
                      ) : (
                        <Text size="xs" className="text-muted-foreground">Not provided</Text>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Phone {isEditing && <span className="text-destructive">*</span>}
                      </Label>
                      {isEditing ? (
                        <Input
                          value={editedData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="Enter phone"
                          disabled={saving}
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="block mt-0.5">{currentData?.phone || 'Not provided'}</Text>
                      )}
                    </div>
                  </Stack>
                </CardContent>
              </Card>

              {/* Experience */}
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Experience</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <Stack spacing="xs" className="gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Experience *</Label>
                      {isEditing ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            placeholder="Years"
                            value={editedData.exp_years}
                            onChange={(e) => handleInputChange('exp_years', e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Input
                            type="number"
                            placeholder="Months"
                            value={editedData.exp_months}
                            onChange={(e) => handleInputChange('exp_months', e.target.value)}
                            className="h-7 text-xs"
                          />
                        </div>
                      ) : (
                        <Text size="xs" className="mt-0.5">
                          {currentData?.exp_years != null
                            ? `${currentData.exp_years} yr ${currentData.exp_months || 0} mo`
                            : 'N/A'}
                        </Text>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Relevant Experience</Label>
                      {isEditing ? (
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            placeholder="Years"
                            value={editedData.relevant_exp_years}
                            onChange={(e) => handleInputChange('relevant_exp_years', e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Input
                            type="number"
                            placeholder="Months"
                            value={editedData.relevant_exp_months}
                            onChange={(e) => handleInputChange('relevant_exp_months', e.target.value)}
                            className="h-7 text-xs"
                          />
                        </div>
                      ) : (
                        <Text size="xs" className="mt-0.5">
                          {currentData?.relevant_exp_years != null
                            ? `${currentData.relevant_exp_years} yr ${currentData.relevant_exp_months || 0} mo`
                            : 'N/A'}
                        </Text>
                      )}
                    </div>
                  </Stack>
                </CardContent>
              </Card>

              {/* Education */}
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Education</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <Stack spacing="xs" className="gap-2">
                    
                    {/* Qualification */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Qualification *</Label>
                      {isEditing ? (
                        <Input
                          value={editedData.qualification}
                          onChange={(e) => handleInputChange('qualification', e.target.value)}
                          placeholder="e.g., B.Tech, MBA"
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="mt-0.5">
                          {currentData?.qualification || 'N/A'}
                        </Text>
                      )}
                    </div>

                    {/* College */}
                    <div>
                      <Label className="text-xs text-muted-foreground">College</Label>
                      {isEditing ? (
                        <Input
                          value={editedData.college}
                          onChange={(e) => handleInputChange('college', e.target.value)}
                          placeholder="College / University"
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="mt-0.5">
                          {currentData?.college || 'N/A'}
                        </Text>
                      )}
                    </div>

                  </Stack>
                </CardContent>
              </Card>

              {/* Locations */}
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Location</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <Stack spacing="xs" className="gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Current {isEditing && <span className="text-destructive">*</span>}
                      </Label>
                      {isEditing ? (
                        <Input
                          value={editedData.cur_loc}
                          onChange={(e) => handleInputChange('cur_loc', e.target.value)}
                          placeholder="Current location"
                          disabled={saving}
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="font-medium block mt-0.5">{currentData?.cur_loc || 'N/A'}</Text>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Preferred {isEditing && <span className="text-destructive">*</span>}
                      </Label>
                      {isEditing ? (
                        <Input
                          value={editedData.preferred_location}
                          onChange={(e) => handleInputChange('preferred_location', e.target.value)}
                          placeholder="Preferred location"
                          disabled={saving}
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="font-medium block mt-0.5">{currentData?.preferred_location || 'N/A'}</Text>
                      )}
                    </div>
                  </Stack>
                </CardContent>
              </Card>

              {/* Current Organization */}
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Organization</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Current Company {isEditing && <span className="text-destructive">*</span>}
                    </Label>
                    {isEditing ? (
                      <Input
                        value={editedData.currentOrganization}
                        onChange={(e) => handleInputChange('currentOrganization', e.target.value)}
                        placeholder="Company name"
                        disabled={saving}
                        className="h-7 text-xs mt-0.5"
                      />
                    ) : (
                      <Text size="xs" className="font-medium block mt-0.5">{currentData?.currentOrganization || 'N/A'}</Text>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Compensation & Availability */}
              <Card className="col-span-2">
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm">Compensation & Availability</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Current CTC {isEditing && <span className="text-destructive">*</span>}
                      </Label>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editedData.cur_ctc}
                          onChange={(e) => handleInputChange('cur_ctc', e.target.value)}
                          placeholder="₹L"
                          disabled={saving}
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="font-medium block mt-0.5">
                          {currentData?.cur_ctc ? `₹${currentData.cur_ctc}L` : 'N/A'}
                        </Text>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Expected CTC {isEditing && <span className="text-destructive">*</span>}
                      </Label>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editedData.exp_ctc}
                          onChange={(e) => handleInputChange('exp_ctc', e.target.value)}
                          placeholder="₹L"
                          disabled={saving}
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="font-medium block mt-0.5">
                          {currentData?.exp_ctc ? `₹${currentData.exp_ctc}L` : 'N/A'}
                        </Text>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Notice Period {isEditing && <span className="text-destructive">*</span>}
                      </Label>
                      {isEditing ? (
                        <Input
                          value={editedData.notice_period}
                          onChange={(e) => handleInputChange('notice_period', e.target.value)}
                          placeholder="e.g., 30 days"
                          disabled={saving}
                          className="h-7 text-xs mt-0.5"
                        />
                      ) : (
                        <Text size="xs" className="font-medium block mt-0.5">{currentData?.notice_period || 'N/A'}</Text>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CandidateProfilePopup;
